import type { ProviderOrAll } from "../../assessment.ts";
import { Providers } from "../../assessment.ts";
import { RiskScores } from "../../catalog.ts";
import type {
  AssessmentNodes,
  CredentialType,
  IdentityType,
  LateralType,
  MfaStatus,
  Usage,
} from "./base.ts";

const TEXT_BOOLEAN_CHOICES = ["false", "true"] as const;

const IdentityTypeChoices: Readonly<Record<IdentityType, string>> = {
  agent: "AI agent",
  "aws-iam-role": "AWS IAM role",
  "aws-permission-set-role": "AWS permission-set role",
  "entra-app-registration": "Entra App Registration",
  domain: "any entity in the provider's domain",
  federated: "federated identity",
  group: "user group",
  "logged-in": "any authenticated identity",
  public: "anyone on the Internet",
  "service-account": "service account",
  "service-principal": "service principal",
  unknown: "unknown",
  user: "user",
};

const MfaStatusChoices: Readonly<Record<MfaStatus, string>> = {
  disabled: "disabled",
  enabled: "enabled",
  unknown: "status unknown",
};

const CredentialTypeChoices: Readonly<Record<CredentialType, string>> = {
  federated: "federated from another IdP",
  key: "static key pair",
  "short-lived": "ephemeral login (via OIDC or impersonation)",
  password: "user password",
};

const LateralTypeChoices: Readonly<Record<LateralType, string>> = {
  grant: "via impersonation privileges",
  resource: "via a service-linked resource",
};

const UsageTypeChoices: Readonly<Record<Usage, string>> = {
  unknown: "usage is unknown",
  unused: "unused in the last 90 days",
  used: "used by the grant principal in the last 90 days.",
};

export type AssessmentSchemaAttribute = {
  choices?: Readonly<Record<string, string>> | readonly string[];
  help?: string;
  hidden?: boolean;
  label?: Record<ProviderOrAll, string> | string;
  type?: "date" | undefined;
};

export type AssessmentSchemaItem<K extends keyof AssessmentNodes> = {
  alias?: string;
  attributes: Readonly<
    Record<keyof AssessmentNodes[K], AssessmentSchemaAttribute>
  >;
  label: string;
};

export const AssessmentSchema: Readonly<{
  [K in keyof AssessmentNodes]: AssessmentSchemaItem<K>;
}> = {
  grant: {
    alias: "entitlement",
    attributes: {
      condition: {
        help: "This grant's condition expression, if present. E.g., an AWS policy-statement condition, Azure role-assignment condition, or GCP role-binding condition.",
      },
      cross: {
        label: "cross-resource access",
        choices: TEXT_BOOLEAN_CHOICES,
        help: "'true' if this principal is managed in a different parent resource than this grant.",
      },
      disabled: { label: "is disabled", choices: TEXT_BOOLEAN_CHOICES },
      isAncestorGrant: {
        label: "is ancestor grant",
        choices: TEXT_BOOLEAN_CHOICES,
        help: "'true' if this grant is inherited from an enclosing scope. E.g., a GCP folder-level grant.",
      },
      isProviderManaged: { hidden: true },
      parent: {
        help: "Resource in which this grant is defined. E.g, an AWS account, Azure subscription, or GCP project.",
      },
      privilegeSet: {
        label: {
          "azure-ad": "role",
          "copilot-studio": "connector",
          "entra-id": "role",
          all: "privilege set",
          aws: "policy",
          azure: "role",
          custom: "privilege set",
          gcp: "role",
          k8s: "role",
          "local-agents": "local agent",
          okta: "role",
          ssh: "privilege",
          workspace: "admin role",
        },
        help: "The privilege set (policy or role) granted by this grant.",
      },
      principal: {
        help: "The identity granted access by this grant.",
      },
      principalType: {
        label: "principal type",
        choices: IdentityTypeChoices,
        help: "The identity type of this grant's principal.",
      },
      provider: {
        label: "provider",
        choices: Providers,
        help: "The provider in which this grant is managed.",
      },
      resources: {
        label: "resource",
        help: "The resource on which access is granted.",
      },
    },
    label: "entitlement",
  } as const,
  condition: {
    attributes: { description: {}, expression: {}, title: {} },
    label: "entitlement condition",
  } as const,
  credential: {
    attributes: {
      type: { label: "credential type", choices: CredentialTypeChoices },
      provider: { label: "credential provider" },
      createdTime: { hidden: true, label: "created at", type: "date" },
      expirationTime: { label: "expiration", type: "date" },
      status: { label: "enablement", choices: ["disabled", "enabled"] },
      lastAuthnTime: { hidden: true, label: "last used at", type: "date" },
      maxAuthnLookbackDays: { hidden: true, label: "max lookback in days" },
    },
    label: "credential",
  } as const,
  consumer: {
    attributes: {
      //organization and userAgents not included here
      type: { label: "consumer type", choices: ["ip", "principal"] },
      label: {},
      lastUsedTime: { hidden: true, label: "last used at", type: "date" },
      identityType: { hidden: true },
      isProviderManaged: { hidden: true },
      provider: { hidden: true },
    },
    label: "credential consumer",
  } as const,
  identity: {
    attributes: {
      // TODO: expand access options
      access: { hidden: true },
      department: {
        help: "Only applies to users. This user's department or organizational unit.",
      },
      disabled: {
        label: "is disabled",
        choices: TEXT_BOOLEAN_CHOICES,
        help: "Whether this identity is disabled.",
      },
      external: {
        label: "external access",
        choices: TEXT_BOOLEAN_CHOICES,
        help: "'true' if this identity is managed outside the assessed scope; 'false' otherwise.",
      },
      isProviderManaged: {
        label: "is provider managed",
        choices: TEXT_BOOLEAN_CHOICES,
        help: "'true' if this identity is fully managed by the provider. E.g., AWS service-linked role or GCP service agent are provider managed.",
      },
      label: {
        help: "This identity's human-readable name. May be the same as 'key' depending on provider.",
      },
      manager: {
        help: "Only applies to users. This user's manager identity.",
      },
      mfa: { label: "MFA status", choices: MfaStatusChoices },
      parent: {
        label: "parent resource",
        help: "The resource in which this identity is managed. E.g., an IdP instance for user accounts, or an Azure subscription, AWS account, or GCP project for cloud service accounts.",
      },
      provider: {
        label: "provider",
        choices: Providers,
        help: "The provider in which this identity is managed.",
      },
      type: {
        label: "type",
        choices: IdentityTypeChoices,
        help: "The type of identity. E.g., user, group, or service account.",
      },
      // Hide as this is a one-off legacy use case
      trustedPrincipals: {
        label: "trust policies",
        help: "Value of this AWS IAM Role's trust policy.",
        hidden: true,
      },
      trustedAccountPrincipals: {
        label: "trusted account principals",
        help: "Any account principals that have access to the role via an Allow statement in the trust policy.",
        hidden: true,
      },
    },
    label: "identity",
  } as const,
  lateral: {
    attributes: {
      identity: {
        label: "accessed identity",
        help: "The key of the accessible identity.",
      },
      constraints: {
        label: "access constraints",
        help: "Constraints that limit the actions that can be taken through the accessible identity",
      },
      resources: {
        help:
          "The accessed resource(s) that allows lateral movement. If the movement is enabled via impersonation privileges, the resource is the IAM provider." +
          " E.g., Azure subscription, AWS account, or GCP project. If the movement is enabled via use of a service identity, the resource will be the linked" +
          " resource. E.g., VM instance.",
      },
      service: {
        help: "The enabling resource's service. E.g. 'compute', or, in the case of impersonation privileges, 'iam' or 'sts'.",
      },
      type: {
        label: "movement category",
        choices: LateralTypeChoices,
        help: "The mechanism by which lateral movement can be exploited; either via impersonation or a service-linked resource.",
      },
    },
    label: "lateral movement path",
  } as const,
  permissionSet: {
    attributes: {
      createdDate: {
        help: "The date and time the permission set was created.",
        label: "created at",
        type: "date",
      },
      sessionDuration: {
        help: "The maximum duration of sessions that can be created using this permission set.",
        label: "session duration",
      },
      label: {
        help: "The human-readable name of the permission set.",
        label: "name",
      },
    },
    label: "AWS permission set",
  },
  privilege: { attributes: {}, label: "privilege" } as const,
  privilegeSet: {
    attributes: {
      provider: {
        label: "provider",
        choices: Providers,
        help: "The provider in which this privilege set is managed.",
      },
    },
    label: "privilege set",
  } as const,
  usage: {
    attributes: {
      type: {
        label: "usage type",
        choices: UsageTypeChoices,
        help: "Privilege usage (within the last 90 days).",
      },
      count: { hidden: true },
    },
    label: "privilege usage",
  } as const,
  resource: {
    attributes: {
      id: {
        help: "The namespaced identifier of this resource.",
      },
      locator: {
        help: "The globally unique identifier of this resource.",
      },
      service: {
        help: "The resource's service. E.g. 'compute'.",
      },
      type: {
        help: "The resource's asset type. E.g. 'instance'.",
      },
      iconUrl: { hidden: true },
    },
    label: "resource",
  } as const,
  risk: {
    attributes: { name: {}, score: { choices: RiskScores } },
    label: "risk",
  } as const,
} as const;
